/**
 * Normalizes user objects from different auth providers (Firebase, OIDC) into
 * a common format. Returns undefined on validation errors for graceful handling.
 */
import type { User } from "firebase/auth";
import type { UserProfile } from "oidc-client-ts";
import { z } from "zod";

import type { NormalizedUser, Tenant } from "../types/auth";

/**
 * Validates OIDC user profile claims. Email is optional since some issuers
 * don't provide it (handled via useSubAsEmail fallback).
 */
const oidcClaimsSchema = z.object({
  sub: z.string(),
  email: z.email().optional(),
  name: z.string().optional(),
  email_verified: z.boolean().optional(),
}) satisfies z.ZodType<Partial<UserProfile> & { sub: string }>;

/**
 * Unified user schema for all auth providers. Fields are nullable where
 * we might not have a value.
 */
const normalizedUserSchema = z.object({
  uid: z.string(),
  email: z.email().nullable(),
  displayName: z.string().nullable(),
  emailVerified: z.boolean(),
  tenantId: z.string().nullable(),
});

interface OidcNormalizationOptions {
  /**
   * Use 'sub' claim as email when issuer doesn't provide email claim.
   */
  useSubAsEmail?: boolean;
}

/**
 * Converts OIDC user profile claims into normalized user format.
 * Returns undefined if profile is invalid.
 */
export const createNormalizedUserFromOidc = (
  tenant: Tenant,
  profile: unknown,
  options: OidcNormalizationOptions = {}
): NormalizedUser | undefined => {
  const { useSubAsEmail = false } = options;

  try {
    const parsed = oidcClaimsSchema.parse(profile);
    const { sub, email: emailClaim, name, email_verified } = parsed;

    // Use 'sub' as email if configured, otherwise use email claim or null
    const email: string | null = useSubAsEmail ? sub : (emailClaim ?? null);

    return normalizedUserSchema.parse({
      uid: sub,
      email,
      displayName: name ?? null,
      emailVerified: email_verified ?? false,
      tenantId: tenant.state === "found" ? tenant.tenantId : null,
    });
  } catch (_error) {
    return undefined;
  }
};

/**
 * Converts Firebase Auth User object into normalized user format.
 * Returns undefined if user is missing or invalid.
 */
export const createNormalizedUserFromFirebase = (
  user?: User
): NormalizedUser | undefined => {
  if (!user) return undefined;

  try {
    return normalizedUserSchema.parse({
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      emailVerified: user.emailVerified,
      tenantId: user.tenantId,
    });
  } catch (_error) {
    return undefined;
  }
};
